import React from 'react';
import debounce from 'p-debounce';
import {
  SectionData,
  SectionDataItem,
} from 'js/components/controls/SelectInput/SectionData';
import {
  ItemResult,
  ItemResultData,
  ItemResultType,
  makeItemResult,
} from 'js/components/controls/search-item-result';
import {
  CommonSearchInput,
  CommonSearchInputHandle,
} from 'js/components/controls/CommonSearchInput';
import { CmsCommonControls } from 'js/model/cms/cms-common-controls';
import { LocationIcon } from 'js/components/controls/LocationSearchInput/LocationIcon';
import { getLocationDisplayName } from 'js/model/view/browse-page/browse-results-summary';
import { Location } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import {
  ChangeBrowseDataFunction,
  changeBrowseDataContext,
  AddLocation,
} from 'js/pages/BrowsePage/BrowseData';
import { fetchLocationSearchResultsDebounced } from 'js/service/searchService';
import { VenueListingCriteria } from 'js/model/rainbow/browse-page/VenueListingCriteria';
import { ClearableMode } from 'js/components/controls/DropdownInput/DropdownInput';
import {
  trackLocationSearch,
  trackLocationSelect,
  LocationType as TrackingLocationType,
  Category,
} from 'js/pages/BrowsePage/WhatWhereWhen/tracking';
import { RequestData } from 'js/helpers/service';
import { CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { arrayWithoutNullables } from 'js/helpers/array';
import { isPositionError } from 'js/helpers/geo-location';

interface Props {
  cms: CmsBrowsePage;
  pageData: RequestData;
  isErrorStyling: boolean;
  location: Location;
  validRadius?: number[];
  otherLocations: Aggregations['locations'];
  trackingCategory: Category;

  /*
    The input will always be rendered, albeit sometimes not visible.
    It can't be conditionally rendered as that would mean that
    in componentDidMount the ref would not be available.

    If the need to use a ref can be eliminated then the input could
    probably be conditionally rendered.
   */
  visible: boolean;
  onFocus?: () => void;
}

export class LocationSearchInput extends React.PureComponent<Props> {
  private commonSearchInputRef =
    React.createRef<CommonSearchInputHandle | null>();

  private selectedResult: ItemResultData | null = null;

  private debouncedSearch = fetchLocationSearchResultsDebounced(200);

  public static contextType = changeBrowseDataContext;

  public componentDidMount(): void {
    this.setSelectedItem();
  }

  public componentDidUpdate(): void {
    this.setSelectedItem();
  }

  private debouncedTrackLocationSearch = debounce(
    (category: Category, searchTerm: string, resultCount: number) => {
      trackLocationSearch(category, searchTerm, resultCount);
    },
    300
  );

  private locationSearchFunction = async (
    searchTerm: string
  ): Promise<ItemResult[]> => {
    const results = await this.debouncedSearch(this.props.pageData, searchTerm);
    /**
     * Event must be tracked in 500ms after user stopped typing, to avoid registering too many irrelevant mid-state typing events.
     * Because we are awaiting for the request to complete, which is debounced after 200ms, we simply chain fire-and-forget
     * debounced tracking call with timeout of 300ms right after results request is completed.
     */
    this.debouncedTrackLocationSearch(
      this.props.trackingCategory,
      searchTerm,
      results.length
    );

    return results;
  };

  private dataForNoSearch(): SectionData<ItemResultData>[] {
    const cmsCommonControls: CmsCommonControls = this.props.cms.common.controls;

    const locationId =
      this.props.location &&
      this.props.location.tree &&
      this.props.location.tree.id;

    // Locations from browse api
    const otherLocationsItems = this.props.otherLocations
      // Remove the current (active) location if it's in the list of other locations.
      // This will prevent it from appearing twice in the drop down.
      .filter((location) => location.id !== locationId)
      .map((location): ItemResult => {
        const result = makeItemResult(
          'filter',
          ItemResultType.Location,
          String(location.id),
          location.name
        );
        result.url = location.uri;
        return result;
      });

    // Current geolocation item
    const geoLocationItem = makeItemResult(
      'filter',
      ItemResultType.CurrentLocation,
      '?',
      cmsCommonControls.search['location-current-location-label']
    );

    // Page location item (if it is not the current gelocation)
    const pageLocationItem = this.pageLocationItem();
    const nonGeolocationPageLocationItem =
      pageLocationItem &&
      pageLocationItem.data.entityType !== ItemResultType.CurrentLocation
        ? this.pageLocationItem()
        : null;

    // Location radius item
    const locationRadiusItem: SectionDataItem<ItemResultData> | undefined = this
      .props.validRadius && {
      type: 'location-radius',
      radius: this.props.location && this.props.location.radius,
      label: cmsCommonControls.search['location-radius-label'],
      nearbyLabel: cmsCommonControls.search['location-radius-nearby'],
      validRadius: this.props.validRadius,
    };

    // Usually this heading is for child locations. They can sometimes be
    // be sibling locations, but that's a minor quibble.
    const childrenHeading: SectionDataItem<ItemResultData> = {
      type: 'label',
      label: cmsCommonControls.search['location-children-heading'],
    };
    const showChildrenHeading =
      nonGeolocationPageLocationItem || otherLocationsItems.length > 0;

    const items: SectionDataItem<ItemResultData>[] = arrayWithoutNullables([
      geoLocationItem,
      locationRadiusItem,
      showChildrenHeading ? childrenHeading : null,
      nonGeolocationPageLocationItem,
      ...otherLocationsItems,
    ]);

    return [{ items }];
  }

  private setSelectedItem(): void {
    const item = this.pageLocationItem();

    if (item) {
      this.setSelectedItemKey(item.key);
    } else {
      this.setSelectedItemKey('');
    }
  }

  public pageLocationItem(): ItemResult | undefined {
    const { location, cms } = this.props;
    const cmsCommonControls: CmsCommonControls = cms.common.controls;

    const text = getLocationDisplayName(cms, location);

    if (location.tree) {
      return makeItemResult(
        'filter',
        ItemResultType.Location,
        String(location.tree.id),
        text
      );
    }

    if (location.postalReference) {
      return makeItemResult(
        'filter',
        ItemResultType.PostalReference,
        String(location.postalReference.id),
        text
      );
    }

    if (location.external) {
      return makeItemResult(
        'filter',
        ItemResultType.ExternalLocation,
        String(location.external.id),
        text
      );
    }

    if (location.point) {
      return makeItemResult(
        'filter',
        ItemResultType.CurrentLocation,
        '?',
        cmsCommonControls.search['location-current-location-label']
      );
    }

    if (location.box) {
      // No item when location is a bounding box.
      return;
    }

    throw new Error('Cannot handle location');
  }

  public clear(): void {
    if (!this.commonSearchInputRef.current) {
      return;
    }

    this.commonSearchInputRef.current.clear();
  }

  public setSelectedItemKey(selectedItemKey: string): void {
    if (!this.commonSearchInputRef.current) {
      return;
    }

    this.commonSearchInputRef.current.setSelectedItemKey(selectedItemKey);
  }

  private onBlur = async () => {
    if (!this.selectedResult) {
      return;
    }

    const id = parseInt(this.selectedResult.entityValue, 10);
    const params: VenueListingCriteria = {};
    let addLocation: AddLocation | undefined;

    switch (this.selectedResult.entityType) {
      case ItemResultType.Location:
      case ItemResultType.PostalArea:
        params.location = {
          tree: {
            id,
          },
        };
        break;

      case ItemResultType.PostalReference:
        params.location = {
          postalReferenceId: id,
        };
        break;

      case ItemResultType.ExternalLocation:
        params.location = {
          external: {
            description: this.selectedResult.name,
            reference: this.selectedResult.entityValue,
          },
        };
        break;

      case ItemResultType.CurrentLocation: {
        trackLocationSelect(
          this.props.trackingCategory,
          TrackingLocationType.Geo
        );
        addLocation = AddLocation.Geo;
        break;
      }
    }

    try {
      const changeBrowseData = this.context as ChangeBrowseDataFunction;

      await changeBrowseData(params, [], addLocation);
    } catch (error) {
      if (isPositionError(error)) {
        if (error.code === error.PERMISSION_DENIED) {
          alert(this.props.cms.common.errors['location-blocked']);
        } else {
          alert(this.props.cms.common.errors['location-error']);
        }
        this.selectedResult = null;
        return;
      }

      throw error;
    }
  };

  private onChange = async (
    value: string,
    selectedKey: string | null,
    data: ItemResultData | null,
    index: number
  ) => {
    const pageLocationItem = this.pageLocationItem();
    if (
      this.props.location &&
      (this.props.location.tree ||
        this.props.location.postalReference ||
        this.props.location.external ||
        this.props.location.point) &&
      pageLocationItem &&
      selectedKey === pageLocationItem.key
    ) {
      // The selected item is the same as the one originally defaulted to.
      this.selectedResult = null;
      return;
    }

    this.selectedResult = data;

    if (this.selectedResult) {
      const id = parseInt(this.selectedResult.entityValue, 10);
      switch (this.selectedResult.entityType) {
        case ItemResultType.Location:
        case ItemResultType.PostalArea:
          trackLocationSelect(
            this.props.trackingCategory,
            TrackingLocationType.Location,
            index,
            id
          );
          break;

        case ItemResultType.PostalReference:
          trackLocationSelect(
            this.props.trackingCategory,
            TrackingLocationType.PostalReference,
            index,
            id
          );
          break;

        case ItemResultType.ExternalLocation:
          trackLocationSelect(
            this.props.trackingCategory,
            TrackingLocationType.ExternalLocation,
            index,
            this.selectedResult.name
          );
          break;
      }
    }
  };

  public render(): React.ReactNode {
    const cmsCommonControls: CmsCommonControls = this.props.cms.common.controls;

    return (
      <CommonSearchInput
        cmsCommonControls={cmsCommonControls}
        ref={this.commonSearchInputRef}
        icon={LocationIcon}
        placeholder={cmsCommonControls.search['location-placeholder-local']}
        searchResultsHeading={
          cmsCommonControls.search['location-results-heading']
        }
        dataForNoSearch={this.dataForNoSearch()}
        searchFunction={this.locationSearchFunction}
        isErrorStyling={this.props.isErrorStyling}
        isHotJarWhiteList
        isClearable={ClearableMode.ClearableWhenActive}
        onChange={this.onChange}
        onBlur={this.onBlur}
        onFocus={this.props.onFocus}
        renderHiddenResults={!this.props.visible}
        isPatternedBackground
      />
    );
  }
}
